/**
 * The sentence, banner tone, and pool-navigation decision for every value
 * `/invite/accept` and `/invite/decline` can answer with, and for every
 * non-pending `/invite/get` status.
 *
 * This file is a deliberate duplicate of the module ISSUE-067's plan
 * specifies for `apps/mobile` at `apps/mobile/src/invites/inviteOutcomeCopy.ts`.
 * There is no shared non-SDK business-logic package for the two clients to
 * import from (`mobile-core` is React Native only), so this is the
 * established convention. Both files must change together: when one message
 * string here changes, the matching string in the mobile file must change
 * with it, byte for byte.
 */
import type { typeInlineBannerVariant } from "@sportsmet/ui";

export type typeInviteOutcomeNotice = {
  message: string;
  variant: typeInlineBannerVariant;
  /** True only for the two outcomes/statuses where the fan is already in the pool. */
  goToPool: boolean;
};

export function inviteAcceptCopy(input: {
  outcome: "joined" | "pendingApproval" | "alreadyMember" | "unavailable";
  poolName: string;
  unavailableReason?: "revoked" | "full" | "closed" | null | undefined;
}): typeInviteOutcomeNotice {
  switch (input.outcome) {
    case "joined":
      return {
        message: `You're in. ${input.poolName} is in your pools now.`,
        variant: "success",
        goToPool: true,
      };
    case "alreadyMember":
      return {
        message: `You're already in ${input.poolName}.`,
        variant: "success",
        goToPool: true,
      };
    case "pendingApproval":
      return {
        message: `Request sent. An admin has to say yes before you're in ${input.poolName}. We'll let you know.`,
        variant: "neutral",
        goToPool: false,
      };
    case "unavailable": {
      const reason: "revoked" | "full" | "closed" =
        input.unavailableReason ?? "revoked";
      switch (reason) {
        case "closed":
          return {
            message: `${input.poolName} is closed to new players.`,
            variant: "warning",
            goToPool: false,
          };
        case "full":
          return {
            message: `${input.poolName} is full.`,
            variant: "warning",
            goToPool: false,
          };
        case "revoked":
          return {
            message:
              "This invite is no longer good. Whoever sent it took it back.",
            variant: "warning",
            goToPool: false,
          };
        default: {
          const exhaustive: never = reason;
          return exhaustive;
        }
      }
    }
    default: {
      const exhaustive: never = input.outcome;
      return exhaustive;
    }
  }
}

export function inviteDeclineCopy(
  outcome: "declined" | "alreadyResolved",
): typeInviteOutcomeNotice {
  switch (outcome) {
    case "declined":
      return {
        message: "You rejected this invite. It's off your list.",
        variant: "neutral",
        goToPool: false,
      };
    case "alreadyResolved":
      return {
        message: "That invite was already answered.",
        variant: "neutral",
        goToPool: false,
      };
    default: {
      const exhaustive: never = outcome;
      return exhaustive;
    }
  }
}

export function inviteStatusNotice(input: {
  status:
    | "pending"
    | "approvalPending"
    | "accepted"
    | "declined"
    | "revoked"
    | "expired";
  poolName: string;
}): typeInviteOutcomeNotice | null {
  switch (input.status) {
    case "pending":
      return null;
    case "approvalPending":
      return {
        message: `Request sent. An admin has to say yes before you're in ${input.poolName}. We'll let you know.`,
        variant: "neutral",
        goToPool: false,
      };
    case "expired":
      return {
        message: "This invite expired.",
        variant: "warning",
        goToPool: false,
      };
    case "revoked":
      return {
        message: "This invite is no longer good. Whoever sent it took it back.",
        variant: "warning",
        goToPool: false,
      };
    case "declined":
      return {
        message: "You rejected this invite. It's off your list.",
        variant: "neutral",
        goToPool: false,
      };
    case "accepted":
      return {
        message: `You already joined ${input.poolName}.`,
        variant: "success",
        goToPool: true,
      };
    default: {
      const exhaustive: never = input.status;
      return exhaustive;
    }
  }
}
